import { useEffect, useState, type FormEvent } from "react";
import { Button, Card, Cluster, FormField, Input, Stack, guardSubmit, useToast } from "@salesapp/ui";
import { reportSaveFailure, reportSaved } from "../lib/saveNotice";
import { orgSettingsApi } from "../lib/orgSettingsApi";
import { useAccess } from "../shell/AccessProvider";
import { FIELD_HINTS } from "../help/fieldHints";

export function NetworkPanel({ orgId }: { orgId: string }) {
  const { show } = useToast();
  const { tier } = useAccess();
  const [cidrs, setCidrs] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [available, setAvailable] = useState(false);
  const [enforced, setEnforced] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    void orgSettingsApi
      .getNetwork(orgId)
      .then((row) => {
        setCidrs(row.cidrs);
        setAvailable(row.available);
        setEnforced(row.enforced);
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Network settings could not be loaded."));
  }, [orgId]);

  async function save(next: string[]) {
    setError(null);
    setNotice(null);
    setSaving(true);
    try {
      const row = await orgSettingsApi.saveNetwork(orgId, next);
      setCidrs(row.cidrs);
      setAvailable(row.available);
      setEnforced(row.enforced);
      setNotice(row.enforced ? "Only these networks can sign in to this organization." : "Allowlist cleared. Any network can sign in.");
      reportSaved(show);
    } catch (err) {
      setError(reportSaveFailure(show, err, "Those addresses could not be saved."));
    } finally {
      setSaving(false);
    }
  }

  function addCidr(event: FormEvent) {
    event.preventDefault();
    const value = draft.trim();
    if (!value) return;
    setDraft("");
    return save([...cidrs, value]);
  }

  return (
    <Card padding="lg">
      <Stack gap={4}>
        <div>
          <h2 className="home-section-title">Approved networks</h2>
          <p className="home-subtitle">
            Enterprise organizations can restrict sign-in to specific office IPs or VPN ranges.
            Leave the list empty to allow any network. This does not change platform admin access.
          </p>
        </div>
        {!available && (
          <p className="home-subtitle">
            IP allowlists are on the Enterprise plan. {tier ? `This organization is on ${tier}.` : "Upgrade to turn this on."}
          </p>
        )}
        {error ? <p className="crm-error">{error}</p> : null}
        {notice ? <p role="status">{notice}</p> : null}
        {enforced ? <p className="home-subtitle">Enforced. People outside these ranges cannot open this tenant.</p> : null}
        <Stack gap={2}>
          {cidrs.length === 0 ? (
            <p className="home-subtitle">No approved networks yet.</p>
          ) : (
            cidrs.map((cidr) => (
              <Cluster key={cidr} gap={3} align="center" justify="between">
                <code>{cidr}</code>
                {available ? (
                  <Button
                    type="button"
                    variant="ghost"
                    disabled={saving}
                    onClick={() => save(cidrs.filter((item) => item !== cidr))}
                  >
                    Remove
                  </Button>
                ) : null}
              </Cluster>
            ))
          )}
        </Stack>
        {available ? (
          <form onSubmit={guardSubmit(addCidr)}>
            <Stack gap={3}>
              <FormField
                label="IP or CIDR"
                htmlFor="org-ip-allow"
                hint="Examples: 203.0.113.10 or 10.0.0.0/24"
                tooltip={FIELD_HINTS.network_allowlist_entry}
              >
                <Input
                  id="org-ip-allow"
                  value={draft}
                  autoComplete="off"
                  onChange={(event) => setDraft(event.target.value)}
                />
              </FormField>
              <Button type="submit" disabled={saving || !draft.trim()}>
                {saving ? "Saving…" : "Add network"}
              </Button>
            </Stack>
          </form>
        ) : null}
      </Stack>
    </Card>
  );
}
